import {
  Badge,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import type { ReactNode } from 'react';

interface NavItemProps {
  icon: ReactNode;
  label: string;
  active: boolean;
  collapsed: boolean;
  // Optional count pill (e.g. unread notifications). Rendered right after the label when
  // expanded; when collapsed there's no label to follow, so it overlays the icon instead —
  // the same placement the notification FAB uses.
  badgeCount?: number;
  onClick: () => void;
}

// Pure prop-driven leaf row — no router awareness. `active` is a plain boolean the caller
// computes (e.g. via useLocation()), so this component stays usable outside react-router.
export function NavItem({ icon, label, active, collapsed, badgeCount, onClick }: NavItemProps) {
  const showBadge = badgeCount !== undefined && badgeCount > 0;

  const button = (
    <ListItemButton
      selected={active}
      onClick={onClick}
      // When collapsed there's no visible text — Tooltip's `title` only wires up
      // aria-describedby on open, it doesn't give the trigger an accessible name. Without
      // this, the button would have no name at all for assistive tech (or role-based queries).
      aria-label={collapsed ? label : undefined}
      // Static sizing (height, padding, margin, radius) lives in theme.ts's MuiListItemButton
      // override — only this prop-dependent bit (centered vs left-aligned) stays local.
      sx={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
    >
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : undefined, justifyContent: 'center' }}>
        {showBadge && collapsed ? (
          <Badge badgeContent={badgeCount} color="error" max={99} overlap="circular">
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </ListItemIcon>
      {!collapsed && (
        <>
          {/* flexGrow: 0 only when badged — otherwise ListItemText's default flex: 1 would
              push the badge to the far right of the row instead of just after the label. */}
          <ListItemText primary={label} sx={showBadge ? { flexGrow: 0, mr: 1 } : undefined} />
          {showBadge && (
            // position: static drops MUI's absolute top-right placement so the pill flows
            // inline after the text; the Badge renders standalone, with no child to anchor to.
            // The size overrides match NotificationFab's pill so the rail badge and the
            // floating bell's badge read as the same thing, and keep the row on one line.
            <Badge
              badgeContent={badgeCount}
              color="error"
              max={99}
              sx={{
                // The root is what sits in the row's flex line — centering here is what keeps
                // the pill level with the label, since `position: static` gives up MUI's own
                // absolute placement.
                display: 'flex',
                alignItems: 'center',
                '& .MuiBadge-badge': {
                  position: 'static',
                  transform: 'none',
                  // A true circle, not MUI's padded pill — equal width/height with no padding,
                  // so the count sits dead centre however many digits it has. 20px holds
                  // "99+" at this font size, which is as wide as it ever gets (see max below).
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  borderRadius: '50%',
                  padding: 0,
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  // MUI's own badge relies on its default height/padding to centre the digits;
                  // once those are overridden the centering has to be spelled out.
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                },
              }}
            />
          )}
        </>
      )}
    </ListItemButton>
  );

  if (!collapsed) {
    return button;
  }

  return (
    <Tooltip title={label} placement="right">
      {button}
    </Tooltip>
  );
}
